
/// <amd-module name="Search/Shared/Components/TreeDropdown/ComboBox" />
import { format } from "VSS/Core/Util/String";
import * as React from "react";
import { css, KeyCode } from "VSSUI/Util";
import { Button } from "VSSUI/Button";
import { CalloutComponent } from "SearchUI/Components/Callout";
import { Spinner, SpinnerSize } from "VSSUI/Spinner";
import { Tooltip } from "VSSUI/TooltipEx";
import { Location } from "VSSUI/Utilities/Position";
import { IComboBoxProps, IComboBox } from "./TreeDropdown.Props";
import { getId } from 'VSS/Core/Util/Object';
import { IconSize } from "VSSUI/Icon";
import * as Resources from "SearchUI/Search.Resources.SearchUI";

export interface IComboBoxState {
    showDisabledInfoCallout: boolean;
}

export class ComboBox extends React.Component<IComboBoxProps, IComboBoxState> implements IComboBox {
    private textBoxRef = React.createRef<HTMLInputElement>();
    private helpInfoRef = React.createRef<HTMLInputElement>();

    constructor(props: IComboBoxProps) {
        super(props)
        this.state = { showDisabledInfoCallout: false };
    }

    public render(): JSX.Element {
        const {
            enabled,
            comboBoxLabel,
            comboBoxText,
            dropdownActive,
            editable,
            applyButtonAriaLabel,
            clearIconAriaLabel,
            onApplyText,
            onChevronClick,
            onClearText,
            showBusy,
            helpIconId,
            disabledInfoCalloutProps,
            treeDropdownId
        } = this.props;
        const showComboBoxLabel = !enabled || !dropdownActive;
        const showInProgressSpinner = enabled && editable && showBusy;
        const showRemoveTextIcon = comboBoxText && dropdownActive && editable && enabled;
        const calloutId = getId("tree-filter-help-callout");

        return (
            <>
                <div className={css("combo-box-container flex-row", !enabled && "is-disabled")}
                    aria-describedby={calloutId}
                    role = "combobox"
                    aria-expanded = {dropdownActive}
                    aria-controls = {treeDropdownId}
                    {...this.getMenuComboBoxAttributes()}>
                    {
                        <div className="labelled-combo-box flex-row flex-grow flex-center">
                            {
                                showComboBoxLabel &&
                                <span className={css("combo-box-label", !enabled && "is-disabled")}>
                                    {`${comboBoxLabel}: `}
                                </span>
                            }
                            <Tooltip
                                anchorOrigin={{ horizontal: Location.center, vertical: Location.end }}
                                text={comboBoxText}
                                tooltipOrigin={{ horizontal: Location.center, vertical: Location.start }}
                            >
                                <span className="flex-grow flex-row flex-center">
                                    <input className={css("combo-box-input flex-grow", !enabled && "is-disabled")}
                                        {...this.getInputBoxAttributes(treeDropdownId)}>
                                    </input>
                                </span>
                            </Tooltip>
                        </div>
                    }
                    {
                        showInProgressSpinner &&
                        <Spinner className="busy-spinner" size={SpinnerSize.small} />
                    }
                    {
                        showRemoveTextIcon &&
                        <Button
                            className="clear-icon"
                            iconProps={{ iconName: "Clear", size: IconSize.small }}
                            subtle={true}
                            onClick={onClearText}
                            ariaLabel={clearIconAriaLabel}
                        />
                    }
                    {
                        !enabled &&
                        <span
                            className="is-disabled disabled-info-icon"
                            id={helpIconId}
                            ref={this.helpInfoRef}
                        />
                    }
                    {
                        showRemoveTextIcon
                            ? <Button
                                className="combo-box-go-btn"
                                iconProps={{ iconName: "Forward", size: IconSize.small }}
                                subtle={true}
                                onClick={onApplyText}
                                ariaLabel={applyButtonAriaLabel}
                            />
                            : <Button
                                className="combo-box-chevron"
                                iconProps={{ iconName: "ChevronDown", size: IconSize.small }}
                                subtle={true}
                                disabled={!enabled}
                                onClick={onChevronClick}
                                ariaLabel={format(Resources.ShowAvailablePath, comboBoxLabel.toLowerCase())}
                            />
                    }
                    {
                        this.state.showDisabledInfoCallout && disabledInfoCalloutProps &&
                        <CalloutComponent
                            targetElement={this.helpInfoRef.current}
                            title={disabledInfoCalloutProps["title"]}
                            content={disabledInfoCalloutProps["content"]}
                            id={calloutId} />
                    }
                </div>
            </>);
    }

    public componentDidMount(): void {
        if (this.props.componentRef) {
            this.props.componentRef(this);
        }
    }

    public componentWillReceiveProps(newProps: IComboBoxProps): void {
        if (newProps.enabled !== this.props.enabled) {
            this.setState({ showDisabledInfoCallout: false });
        }
    }

    public getInputRef = (): HTMLElement | null => {
        return this.textBoxRef.current;
    }

    private getMenuComboBoxAttributes(): { [key: string]: any } {
        const { enabled, comboBoxLabel, onComboBoxClick } = this.props;

        let attributes: { [key: string]: any } = {
            "aria-label": comboBoxLabel
        };

        if (enabled) {
            attributes["onClick"] = onComboBoxClick;
        } else {
            attributes["onFocus"] = () => this.triggerHelpCallout(true);
            attributes["onBlur"] = () => this.triggerHelpCallout(false);
            attributes["tabIndex"] = (!enabled ? -1 : 0);
            attributes["aria-disabled"] = true;
            attributes["onMouseOver"] = () => this.triggerHelpCallout(true);
            attributes["onMouseLeave"] = () => this.triggerHelpCallout(false);
            attributes["onKeyDown"] = this.onKeyDown;
        }

        return attributes;
    }

    /**
     * This function provides the attributes of the input element of the path control based on its state
     * When the control is enabled, the input box should behave like a combobox and is the first tabbable element
     */
    private getInputBoxAttributes(ariaOwnsId?: string): { [key: string]: any } {
        const {
            comboBoxLabel,
            comboBoxText,
            enabled,
            dropdownActive,
            onComboBoxClick,
            editable,
            onTextChanged,
            onInputFocus,
            placeholder,
            onInputKeyDown
        } = this.props;
        const label = `${comboBoxLabel} ${comboBoxText}`;
        let attributes: { [key: string]: any } = {
            "ref": this.textBoxRef,
            "aria-label": label,
            "spellCheck": false
        };

        if (enabled) {
            attributes["aria-expanded"] = dropdownActive;
            attributes["role"] = "combobox";
            attributes["aria-autocomplete"] = "list";
            attributes["value"] = comboBoxText;
            attributes["onClick"] = onComboBoxClick;
            attributes["aria-controls"] = ariaOwnsId;

            if (editable) {
                attributes["onChange"] = onTextChanged;
                attributes["onFocus"] = onInputFocus;
                attributes["placeholder"] = placeholder;
                attributes["onKeyDown"] = onInputKeyDown;
            } else {
                // Make input readonly not disabled. 
                // If the input is disabled click event is not fired in I.E/Edge.
                attributes["readOnly"] = true;
            }
        } else {
            attributes["disabled"] = true;
            attributes["value"] = "";
        }

        return attributes;
    }

    private triggerHelpCallout = (showCallout: boolean): void => {
        const { enabled } = this.props;
        if (!enabled) {
            this.setState({ showDisabledInfoCallout: showCallout });
        }
    }

    /**
     * Handle Keyboard navigation when focus is on disabled filter bar
     * @param evt
     */
    private onKeyDown = (evt:React.KeyboardEvent<HTMLElement>): void => {
        if (evt.keyCode === KeyCode.escape) {
            this.triggerHelpCallout(false);
        }
    }
}
